import React, { useEffect, useState } from "react";
import "./MainPage.scss";
import WhatIs from "../WhatIs/WhatIs";
import HowToUse from "../HowToUse/HowToUse";
import Contact from "../Contact/Contact";
import Offer from "../Offer/Offer";
import NekrologTeaser from "../NekrologTeaser/NekrologTeaser";
import logo from "../../../img/nekrodruk-logo-dark.png";
import backgroundMainPage from "../../../img/backgroundMainPage.jpg";
import backgroundMainPageLow from "../../../img/backgroundMainPageLow.jpg";
import { Helmet } from "react-helmet-async";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { Link as RouterLink } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

const HERO_POINTS = [
  "Nekrologi, tabliczki i szarfy z jednego formularza",
  "Gotowe szablony i szybka personalizacja dla zakładów pogrzebowych",
  "Eksport do druku i materiałów cyfrowych bez dodatkowego oprogramowania",
];

const BENEFIT_CARDS = [
  {
    title: "Spokojniejsza obsługa rodzin",
    text: "W trudnym momencie liczy się pewność i tempo. Nekrodruk porządkuje dane i skraca drogę od rozmowy do gotowego wydruku.",
  },
  {
    title: "Jednolity standard materiałów",
    text: "Powtarzalny wygląd nekrologów i pozostałych materiałów pomaga utrzymać profesjonalny wizerunek zakładu.",
  },
  {
    title: "Mniej ręcznej pracy",
    text: "Nie trzeba składać wszystkiego od zera przy każdym zleceniu. Zapisujesz ustawienia, wracasz do wzorów i generujesz szybciej.",
  },
];

const USE_CASES = [
  {
    eyebrow: "Dla zakładów pogrzebowych",
    title: "Narzędzie do codziennej pracy biura i obsługi klienta",
    text: "Twórz materiały bez przełączania się między programami. Jeden pracownik może sprawnie przygotować projekt, korektę i finalny plik do druku.",
  },
  {
    eyebrow: "Dla punktów druku i partnerów",
    title: "Szybsza współpraca przy tabliczkach i materiałach ceremonii",
    text: "Przejrzysty proces i gotowe formaty ułatwiają przekazanie pliku do druku oraz ograniczają liczbę poprawek przy zamówieniu.",
  },
  {
    eyebrow: "Dla nowoczesnych wdrożeń",
    title: "Gotowość do eksportu i archiwizacji materiałów",
    text: "Te same dane można wykorzystać przy przygotowaniu plików do druku, zapisie projektu i późniejszym powrocie do sprawy.",
  },
];

const FAQ_ITEMS = [
  {
    question: "Czy Nekrodruk jest przeznaczony tylko dla zakładów pogrzebowych?",
    answer:
      "To rozwiązanie projektowane przede wszystkim dla zakładów pogrzebowych, ale sprawdzi się też tam, gdzie przygotowuje się materiały funeralne do druku lub publikacji.",
  },
  {
    question: "Jakie materiały można przygotować w aplikacji?",
    answer:
      "System pozwala tworzyć nekrologi, tabliczki na krzyż, szarfy oraz powiązane materiały eksportowane do dalszego wykorzystania i druku.",
  },
  {
    question: "Czy trzeba instalować dodatkowe programy graficzne?",
    answer:
      "Nie. Aplikacja działa w przeglądarce, a personalizacja projektu odbywa się bez potrzeby korzystania z zewnętrznych narzędzi DTP.",
  },
  {
    question: "Czy mogę przetestować system przed rejestracją?",
    answer:
      "Tak, na stronie głównej dostępne jest demo, które pozwala poznać sposób działania generatora bez logowania.",
  },
];

export default function MainPage() {
  const [loading, setLoading] = useState(true);
  const [bgImage, setBgImage] = useState("");

  useEffect(() => {
    const minimumLoadingTime = 1000;
    const startTime = Date.now();

    const handlePageLoad = () => {
      const timeElapsed = Date.now() - startTime;
      const remainingTime = minimumLoadingTime - timeElapsed;

      if (remainingTime > 0) {
        setTimeout(() => setLoading(false), remainingTime);
      } else {
        setLoading(false);
      }
    };

    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      window.addEventListener("load", handlePageLoad);
    }

    return () => window.removeEventListener("load", handlePageLoad);
  }, []);

  useEffect(() => {
    const lowRes = new Image();
    lowRes.src = backgroundMainPageLow;
    lowRes.onload = () => {
      setBgImage(backgroundMainPageLow);

      const highRes = new Image();
      highRes.src = backgroundMainPage;
      highRes.onload = () => setBgImage(backgroundMainPage);
    };
  }, []);

  const pulse = keyframes`
        0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(81, 137, 176, 0.7);
        }
        70% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(81, 137, 176, 0);
        }
        100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(81, 137, 176, 0);
        }
        `;

  if (loading) {
    return (
      <Box
        className="loading_section"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <img
          src={logo}
          alt="loading_logo"
          className="loading_logo"
          style={{ maxWidth: 200, marginBottom: 20 }}
        />
        <CircularProgress size={50} color="primary" />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Nekrodruk.pl - Generator nekrologów, tabliczek i szarf online
        </title>
        <meta
          name="description"
          content="Nekrodruk.pl to generator nekrologów online dla zakładów pogrzebowych. Twórz nekrologi, tabliczki i szarfy szybciej, korzystaj z gotowych szablonów, personalizacji i eksportu do druku."
        />
        <meta
          name="keywords"
          content="generator nekrologów, nekrologi online, klepsydry, tabliczki na krzyż, szarfy pogrzebowe, program dla zakładu pogrzebowego, druk nekrologów"
        />
        <meta name="author" content="Nekrodruk.pl" />
        <meta
          property="og:title"
          content="Nekrodruk.pl - Generator nekrologów online"
        />
        <meta
          property="og:description"
          content="Szybkie tworzenie nekrologów, tabliczek i szarf dla zakładów pogrzebowych. Gotowe szablony, personalizacja i demo bez logowania."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nekrodruk.pl/" />
        <link rel="canonical" href="https://nekrodruk.pl/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Nekrodruk",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "Aplikacja internetowa do tworzenia nekrologów, tabliczek na krzyż i szarf pogrzebowych.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "PLN",
              description: "Możliwość sprawdzenia wersji demo",
            },
          })}
        </script>
      </Helmet>
      <section className="mainPage">
        <div
          className="mainPage_grid"
          style={{ backgroundImage: `url(${bgImage})` }}
        >
          <div className="mainPage_overlay" />
          <div className="item1">
            <div className="item1_txt">
              <span className="mainPage_kicker">
                Platforma dla zakładów pogrzebowych
              </span>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                Generator nekrologów, tabliczek i szarf
                <br />
                gotowy do pracy od pierwszego dnia.
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 1200 }}>
                Nekrodruk pomaga szybko przygotować materiały funeralne w
                jednolitym standardzie. Wprowadź dane raz, dobierz szablon,
                dopracuj wygląd i od razu przejdź do druku.
              </Typography>
              <ul className="mainPage_points">
                {HERO_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>

            <div className="mainPage_ctaRow">
              <Box
                mt={4}
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={2}
              >
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/register"
                  sx={{
                    backgroundColor: "#5189B0",
                    color: "#fff",
                    px: 4,
                    borderRadius: 3,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    animation: `${pulse} 2.5s infinite`,
                    "&:hover": {
                      backgroundColor: "#63b3be",
                    },
                  }}
                >
                  Załóż konto
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/demo"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    textTransform: "none",
                    fontSize: "1.1rem",
                    color: "#5189B0",
                    borderColor: "#5189B0",
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#e3f7f9",
                      borderColor: "#63b3be",
                      color: "#63b3be",
                    },
                  }}
                >
                  Wypróbuj demo bez logowania
                </Button>
              </Box>

              <div className="mainPage_meta">
                <span>Bez instalacji</span>
                <span>Praca w przeglądarce</span>
                <span>Wsparcie wdrożenia</span>
              </div>
            </div>
          </div>
        </div>

        <section className="mainPage_storyBand">
          <div className="mainPage_storyBandInner">
            <div className="mainPage_storyCopy">
              <span className="mainPage_sectionKicker">Dlaczego to działa</span>
              <h2>
                Szybsze przygotowanie materiałów oznacza więcej czasu na
                spokojną i profesjonalną obsługę rodzin.
              </h2>
            </div>
            <div className="mainPage_storyGrid">
              {BENEFIT_CARDS.map((card) => (
                <article key={card.title} className="mainPage_storyCard">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div className="mainPage_wrapper">
          <Offer />
          <WhatIs />
          <HowToUse />
        </div>

        <NekrologTeaser />

        <section className="mainPage_showcase">
          <div className="mainPage_showcaseInner">
            <div className="mainPage_showcaseHeader">
              <span className="mainPage_sectionKicker">Zastosowania</span>
              <h2>Jedno narzędzie, wiele scenariuszy pracy</h2>
              <p>
                Nekrodruk wspiera codzienną pracę od pierwszej rozmowy z rodziną
                po przygotowanie gotowych plików do druku.
              </p>
            </div>
            <div className="mainPage_showcaseGrid">
              {USE_CASES.map((item) => (
                <article key={item.title} className="mainPage_showcaseCard">
                  <span>{item.eyebrow}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mainPage_guides">
          <div className="mainPage_guidesInner">
            <div className="mainPage_guidesText">
              <span className="mainPage_sectionKicker">Poradniki i wsparcie</span>
              <h2>Pomagamy szybciej przygotować poprawne materiały</h2>
              <p>
                Oprócz samego generatora udostępniamy także praktyczne
                wskazówki dotyczące treści nekrologów, przygotowania klepsydr i
                organizacji druku. Dzięki temu łatwiej zachować poprawność
                danych i uniknąć kosztownych pomyłek.
              </p>
              <div className="mainPage_guidesActions">
                <RouterLink to="/co-to-nekrolog" className="mainPage_guidesBtn">
                  Zobacz poradnik o nekrologach
                </RouterLink>
                <HashLink
                  to="/#Contact"
                  className="mainPage_guidesGhostBtn"
                >
                  Porozmawiaj o wdrożeniu
                </HashLink>
              </div>
            </div>
            <div className="mainPage_guidesVisual" aria-hidden="true">
              <div className="mainPage_guidesPanel">
                <span>Poradnik</span>
                <p>
                  Wyjaśniamy, jakie informacje powinien zawierać poprawny
                  nekrolog i jak ułożyć treść do druku.
                </p>
              </div>
              <div className="mainPage_guidesPanel">
                <span>Praktyka</span>
                <p>
                  Pokazujemy, jak przyspieszyć przygotowanie materiałów bez
                  utraty estetyki i czytelności.
                </p>
              </div>
              <div className="mainPage_guidesPanel">
                <span>Wsparcie</span>
                <p>
                  W razie pytań pomagamy wdrożyć system i dopasować go do
                  codziennej pracy zakładu.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mainPage_faq">
          <div className="mainPage_faqInner">
            <div className="mainPage_faqHeader">
              <span className="mainPage_sectionKicker">FAQ</span>
              <h2>Najczęściej zadawane pytania o Nekrodruk</h2>
              <p>
                Zebraliśmy najważniejsze odpowiedzi, z którymi najczęściej
                startują nowe zakłady i osoby testujące system.
              </p>
            </div>
            <div className="mainPage_faqGrid">
              {FAQ_ITEMS.map((item) => (
                <article key={item.question} className="mainPage_faqCard">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div className="mainPage_wrapper">
          <Contact />
        </div>
      </section>
    </>
  );
}
